import React, { useState } from "react";
import {
  Button,
  Input,
  Table,
  Typography,
  message,
  Card,
  Space,
  Tag,
  Alert,
  Spin,
  Select,
  Form,
  Modal,
  Radio,
  List,
  Popconfirm,
  Collapse,
} from "antd";
import { DownloadOutlined, SaveOutlined, LinkOutlined, EditOutlined, RobotOutlined } from "@ant-design/icons";
import axios from "axios";
import { useDialog } from "./DialogProvider";

const { Panel } = Collapse;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function WorkoutParser() {
  const [mode, setMode] = useState("single"); // "single" or "program"
  const [url, setUrl] = useState("");
  const [programUrls, setProgramUrls] = useState([]);
  const [parsedData, setParsedData] = useState(null);
  const [parsedWorkouts, setParsedWorkouts] = useState([]);
  const [reformulatedData, setReformulatedData] = useState(null);
  const [reformulatedWorkouts, setReformulatedWorkouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [parsingMultiple, setParsingMultiple] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reformulating, setReformulating] = useState(false);
  const [reformulatingMultiple, setReformulatingMultiple] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [programCategory, setProgramCategory] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const { alertDlg } = useDialog();

  // Create axios instance with interceptors for token handling and retries
  const axiosInstance = axios.create({
    baseURL: "/api",
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Attach token to every request if available
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Handle response errors with token refresh and retry logic
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Check if the error is due to an expired access token
      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true; // Prevent infinite retries

        try {
          // Call /refresh_tokens/ to get new tokens
          const refreshToken = localStorage.getItem("refresh_token");
          const refreshResponse = await axios.post("api/refresh_tokens/", {
            refresh_token: refreshToken,
          });

          if (refreshResponse.status === 200) {
            const { access_token: newToken, refresh_token: newRefreshToken } =
              refreshResponse.data;

            // Store new tokens in localStorage
            localStorage.setItem("token", newToken);
            localStorage.setItem("refresh_token", newRefreshToken);

            // Update Authorization header and retry the original request
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          }
        } catch (refreshError) {
          alertDlg("You've been inactive for too long. You might need to log in again.");
          console.error("Failed to refresh token:", refreshError);
          // Don't remove tokens here to avoid logout loops
        }
      }

      return Promise.reject(error);
    }
  );

  const parseWorkout = async () => {
    if (!url.trim()) {
      message.error("Please enter a URL");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post("/admin/parse_workout/", {
        url: url.trim(),
      });
      setParsedData(response.data);
      message.success("Workout parsed successfully!");
    } catch (error) {
      console.error("Parse error:", error);
      message.error(
        error.response?.data?.detail || "Failed to parse workout"
      );
    } finally {
      setLoading(false);
    }
  };

  const saveWorkout = async (dataToSave = null) => {
    const workoutData = dataToSave || reformulatedData || parsedData;
    if (!workoutData) {
      message.error("No parsed data to save");
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.post("/admin/save_parsed_workout/", workoutData);
      message.success("Workout saved to SimplyRide successfully!");
    } catch (error) {
      console.error("Save error:", error);
      message.error(
        error.response?.data?.detail || "Failed to save workout"
      );
    } finally {
      setSaving(false);
    }
  };

  const reformulateWorkout = async () => {
    if (!parsedData) {
      message.error("No parsed data to reformulate");
      return;
    }

    setCategoryModalVisible(true);
  };

  const confirmReformulate = async () => {
    if (!parsedData) return;

    setReformulating(true);
    setCategoryModalVisible(false);

    try {
      const response = await axiosInstance.post("/admin/reformulate_workout/", {
        name: parsedData.title,
        category: parsedData.category,
        description: parsedData.description,
        url: parsedData.url,
        new_category: selectedCategory || undefined,
      });

      const reformulated = {
        ...parsedData,
        title: response.data.reformulated_name,
        category: response.data.reformulated_category,
        description: response.data.reformulated_description,
      };

      setReformulatedData(reformulated);
      message.success("Workout reformulated successfully!");
    } catch (error) {
      console.error("Reformulate error:", error);
      message.error(
        error.response?.data?.detail || "Failed to reformulate workout"
      );
    } finally {
      setReformulating(false);
      setSelectedCategory("");
    }
  };

  const handleTableEdit = (record, field, value) => {
    const currentData = reformulatedData || parsedData;
    if (!currentData) return;

    const updatedData = { ...currentData };

    if (field === 'title') {
      updatedData.title = value;
    } else if (field === 'category') {
      updatedData.category = value;
    } else if (field === 'description') {
      updatedData.description = value;
    } else if (field.startsWith('step_')) {
      const [, stepIndex, stepField] = field.split('_');
      const index = parseInt(stepIndex);
      if (updatedData.steps && updatedData.steps[index]) {
        updatedData.steps[index][stepField] = stepField === 'duration' || stepField === 'rpm' ? parseInt(value) || 0 : value;
      }
    }

    if (reformulatedData) {
      setReformulatedData(updatedData);
    } else {
      setParsedData(updatedData);
    }
  };

  const scrapeProgramUrls = async () => {
    if (!url.trim()) {
      message.error("Please enter a program URL");
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.post("/admin/scrape_program_urls/", {
        program_url: url.trim(),
      });

      setProgramUrls(response.data.workout_urls);
      setProgramCategory(response.data.category);
      message.success(`Found ${response.data.total_found} workout URLs`);
    } catch (error) {
      console.error("Scrape error:", error);
      message.error(
        error.response?.data?.detail || "Failed to scrape program URLs"
      );
    } finally {
      setLoading(false);
    }
  };

  const addUrl = () => {
    setProgramUrls([...programUrls, ""]);
  };

  const updateUrl = (index, newUrl) => {
    const updated = [...programUrls];
    updated[index] = newUrl;
    setProgramUrls(updated);
  };

  const removeUrl = (index) => {
    const updated = programUrls.filter((_, i) => i !== index);
    setProgramUrls(updated);
  };

  const parseMultipleWorkouts = async () => {
    if (programUrls.length === 0) {
      message.error("No workout URLs to parse");
      return;
    }

    // Filter out empty URLs
    const validUrls = programUrls.filter(url => url.trim());
    if (validUrls.length === 0) {
      message.error("No valid workout URLs to parse");
      return;
    }

    setParsingMultiple(true);
    try {
      const response = await axiosInstance.post("/admin/parse_multiple_workouts/", {
        workout_urls: validUrls,
      });

      const successfulWorkouts = response.data.results
        .filter(r => r.success)
        .map(r => r.data);

      setParsedWorkouts(successfulWorkouts);

      message.success(
        `Parsed ${response.data.successful_parses} workouts successfully (${response.data.failed_parses} failed)`
      );

      if (response.data.failed_parses > 0) {
        message.warning(`${response.data.failed_parses} workouts failed to parse`);
      }
    } catch (error) {
      console.error("Parse multiple error:", error);
      message.error(
        error.response?.data?.detail || "Failed to parse multiple workouts"
      );
    } finally {
      setParsingMultiple(false);
    }
  };

  const reformulateMultipleWorkouts = async () => {
    if (parsedWorkouts.length === 0) {
      message.error("No workouts to reformulate");
      return;
    }

    setReformulatingMultiple(true);
    try {
      const response = await axiosInstance.post("/admin/reformulate_multiple_workouts/", {
        workouts: parsedWorkouts,
        new_category: programCategory || undefined,
      });

      const successfulReformulations = response.data.results
        .filter(r => r.success)
        .map(r => r.reformulated);

      setReformulatedWorkouts(successfulReformulations);

      message.success(
        `Reformulated ${response.data.successful_reformulations} workouts successfully`
      );

      if (response.data.failed_reformulations > 0) {
        message.warning(`${response.data.failed_reformulations} workouts failed to reformulate`);
      }
    } catch (error) {
      console.error("Reformulate multiple error:", error);
      message.error(
        error.response?.data?.detail || "Failed to reformulate multiple workouts"
      );
    } finally {
      setReformulatingMultiple(false);
    }
  };

  const saveMultipleWorkouts = async () => {
    const workoutsToSave = reformulatedWorkouts.length > 0 ? reformulatedWorkouts : parsedWorkouts;

    if (workoutsToSave.length === 0) {
      message.error("No workouts to save");
      return;
    }

    setSaving(true);
    try {
      const response = await axiosInstance.post("/admin/save_multiple_workouts/", {
        workouts: workoutsToSave,
      });

      message.success(
        `Saved ${response.data.saved_workouts} workouts successfully to SimplyRide`
      );

      if (response.data.failed_workouts > 0) {
        message.warning(`${response.data.failed_workouts} workouts failed to save`);
      }
    } catch (error) {
      console.error("Save multiple error:", error);
      message.error(
        error.response?.data?.detail || "Failed to save multiple workouts"
      );
    } finally {
      setSaving(false);
    }
  };

  const updateWorkoutField = (index, field, value) => {
    const currentWorkouts = reformulatedWorkouts.length > 0 ? reformulatedWorkouts : parsedWorkouts;
    if (!currentWorkouts[index]) return;

    const updated = [...currentWorkouts];
    updated[index] = { ...updated[index], [field]: value };

    if (reformulatedWorkouts.length > 0) {
      setReformulatedWorkouts(updated);
    } else {
      setParsedWorkouts(updated);
    }
  };

  const updateWorkoutStep = (workoutIndex, stepIndex, field, value) => {
    const currentWorkouts = reformulatedWorkouts.length > 0 ? reformulatedWorkouts : parsedWorkouts;
    if (!currentWorkouts[workoutIndex] || !currentWorkouts[workoutIndex].steps) return;

    const updatedWorkouts = [...currentWorkouts];
    const updatedSteps = [...updatedWorkouts[workoutIndex].steps];

    if (field.startsWith('step_')) {
      const [, , stepField] = field.split('_');
      updatedSteps[stepIndex] = {
        ...updatedSteps[stepIndex],
        [stepField]: stepField === 'duration' || stepField === 'rpm' ? parseInt(value) || 0 : value
      };
    } else {
      updatedSteps[stepIndex] = { ...updatedSteps[stepIndex], [field]: value };
    }

    updatedWorkouts[workoutIndex] = { ...updatedWorkouts[workoutIndex], steps: updatedSteps };

    if (reformulatedWorkouts.length > 0) {
      setReformulatedWorkouts(updatedWorkouts);
    } else {
      setParsedWorkouts(updatedWorkouts);
    }
  };

  const applyCategoryToAll = (newCategory) => {
    const currentWorkouts = reformulatedWorkouts.length > 0 ? reformulatedWorkouts : parsedWorkouts;
    const updated = currentWorkouts.map(workout => ({
      ...workout,
      category: newCategory
    }));

    if (reformulatedWorkouts.length > 0) {
      setReformulatedWorkouts(updated);
    } else {
      setParsedWorkouts(updated);
    }
  };

  const createStepColumns = (workoutIndex) => [
    {
      title: "Step",
      dataIndex: "index",
      key: "index",
      width: 80,
      render: (text, record, index) => index + 1,
    },
    {
      title: "Duration (sec)",
      dataIndex: "duration",
      key: "duration",
      width: 120,
      render: (duration, record, index) => (
        <Input
          value={duration || ""}
          onChange={(e) => workoutIndex === -1
            ? handleTableEdit(record, `step_${index}_duration`, e.target.value)
            : updateWorkoutStep(workoutIndex, index, `step_${index}_duration`, e.target.value)}
          placeholder="Duration"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: "Power",
      key: "power",
      width: 150,
      render: (record, index) => {
        if (record.repeat) {
          return (
            <div>
              <Tag color="blue">Repeat {record.repeat}x</Tag>
              <div style={{ marginTop: 4 }}>
                {record.steps?.map((step, idx) => (
                  <div key={idx} style={{ fontSize: "12px" }}>
                    <Input
                      value={`${step.duration}s @ ${step.watt || step.from}% FTP${step.rpm > 0 ? ` (${step.rpm} RPM)` : ''}`}
                      onChange={(e) => workoutIndex === -1
                        ? handleTableEdit(record, `step_${index}_power_${idx}`, e.target.value)
                        : updateWorkoutStep(workoutIndex, index, `step_${index}_power_${idx}`, e.target.value)}
                      placeholder="Power details"
                      style={{ width: '100%', fontSize: '12px', marginBottom: 2 }}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        }

        if (record.from !== undefined && record.to !== undefined) {
          return (
            <Input
              value={`${record.from}-${record.to}% FTP`}
              onChange={(e) => workoutIndex === -1
                ? handleTableEdit(record, `step_${index}_power`, e.target.value)
                : updateWorkoutStep(workoutIndex, index, `step_${index}_power`, e.target.value)}
              placeholder="Power range"
              style={{ width: '100%' }}
            />
          );
        }

        return (
          <Input
            value={record.watt ? `${record.watt}% FTP` : ""}
            onChange={(e) => workoutIndex === -1
              ? handleTableEdit(record, `step_${index}_watt`, e.target.value)
              : updateWorkoutStep(workoutIndex, index, `step_${index}_watt`, e.target.value)}
            placeholder="Power %"
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: "RPM",
      dataIndex: "rpm",
      key: "rpm",
      width: 80,
      render: (rpm, record, index) => (
        <Input
          value={rpm > 0 ? rpm : ""}
          onChange={(e) => workoutIndex === -1
            ? handleTableEdit(record, `step_${index}_rpm`, e.target.value)
            : updateWorkoutStep(workoutIndex, index, `step_${index}_rpm`, e.target.value)}
          placeholder="RPM"
          style={{ width: '100%' }}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <Title level={2}>
        <DownloadOutlined style={{ marginRight: 8 }} />
        Workout Parser
      </Title>

      <Card style={{ marginBottom: 20 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text strong>Mode:</Text>
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
            <Radio value="single">Single Workout</Radio>
            <Radio value="program">Program Scraper</Radio>
          </Radio.Group>
        </Space>
      </Card>

      <Card style={{ marginBottom: 20 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {mode === "single" ? (
            <>
              <Text strong>Enter Workout URL:</Text>
              <Input
                placeholder="https://whatsonzwift.com/workouts/plan-name/week-1-1-workout-name"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                prefix={<LinkOutlined />}
                onPressEnter={parseWorkout}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 For workout plans, use individual workout URLs rather than plan overview URLs
              </Text>
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={parseWorkout}
                  loading={loading}
                  disabled={!url.trim()}
                >
                  Parse Workout
                </Button>
                {parsedData && (
                  <Button
                    type="default"
                    icon={<RobotOutlined />}
                    onClick={reformulateWorkout}
                    loading={reformulating}
                  >
                    Reformulate with GPT
                  </Button>
                )}
              </Space>
            </>
          ) : (
            <>
              <Text strong>Enter Program URL:</Text>
              <Input
                placeholder="https://whatsonzwift.com/workouts/gran-fondo"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                prefix={<LinkOutlined />}
                onPressEnter={scrapeProgramUrls}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                💡 Enter the main program page URL to extract all individual workout URLs
              </Text>
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={scrapeProgramUrls}
                  loading={loading}
                  disabled={!url.trim()}
                >
                  Scrape Program URLs
                </Button>
                {programUrls.length > 0 && (
                  <>
                    <Button
                      type="default"
                      onClick={parseMultipleWorkouts}
                      loading={parsingMultiple}
                    >
                      Parse All Workouts ({programUrls.length})
                    </Button>
                    {parsedWorkouts.length > 0 && (
                      <Button
                        type="default"
                        icon={<RobotOutlined />}
                        onClick={reformulateMultipleWorkouts}
                        loading={reformulatingMultiple}
                      >
                        Reformulate All with GPT
                      </Button>
                    )}
                  </>
                )}
              </Space>
            </>
          )}
        </Space>
      </Card>

      {/* URL Management for Program Mode */}
      {mode === "program" && programUrls.length > 0 && (
        <Card style={{ marginBottom: 20 }}>
          <Title level={4}>Workout URLs ({programUrls.length})</Title>
          <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
            Category: <strong>{programCategory}</strong>
          </Text>

          <List
            dataSource={programUrls}
            renderItem={(url, index) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="Delete this URL?"
                    onConfirm={() => removeUrl(index)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button size="small" danger>Delete</Button>
                  </Popconfirm>
                ]}
              >
                <Input
                  value={url}
                  onChange={(e) => updateUrl(index, e.target.value)}
                  placeholder="Enter workout URL"
                  style={{ width: '100%' }}
                />
              </List.Item>
            )}
          />

          <Button
            type="dashed"
            onClick={addUrl}
            style={{ width: '100%', marginTop: 8 }}
          >
            Add URL
          </Button>
        </Card>
      )}

      {/* Single Workout Display */}
      {mode === "single" && parsedData && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <Title level={3}>
              Workout Information
              {(reformulatedData || parsedData) && (
                <Tag color={reformulatedData ? "green" : "blue"} style={{ marginLeft: 8 }}>
                  {reformulatedData ? "Reformulated" : "Original"}
                </Tag>
              )}
            </Title>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <Text strong>Title: </Text>
                <Input
                  value={(reformulatedData || parsedData).title}
                  onChange={(e) => handleTableEdit(null, 'title', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Text strong>Category: </Text>
                <Input
                  value={(reformulatedData || parsedData).category}
                  onChange={(e) => handleTableEdit(null, 'category', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Text strong>Stress Points: </Text>
                <Text>{(reformulatedData || parsedData).stress_points}</Text>
              </div>
              <div>
                <Text strong>Duration per Zone: </Text>
                <Text>{(reformulatedData || parsedData).duration_per_zone}</Text>
              </div>
              <div>
                <Text strong>Description: </Text>
                <Input.TextArea
                  value={(reformulatedData || parsedData).description}
                  onChange={(e) => handleTableEdit(null, 'description', e.target.value)}
                  rows={4}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Text strong>Source URL: </Text>
                <a href={(reformulatedData || parsedData).url} target="_blank" rel="noopener noreferrer">
                  {(reformulatedData || parsedData).url}
                </a>
              </div>
            </Space>
          </Card>

          <Card style={{ marginBottom: 20 }}>
            <Title level={3}>Workout Steps ({parsedData.steps?.length || 0})</Title>
            <Table
              columns={createStepColumns(-1)} // -1 for single workout mode
              dataSource={(reformulatedData || parsedData).steps || []}
              rowKey={(record, index) => index}
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
            />
          </Card>

          <Card>
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => saveWorkout(reformulatedData || parsedData)}
                loading={saving}
                size="large"
              >
                Save to SimplyRide
              </Button>
              <Alert
                message="This will save the workout under the 'simplyride' username in BigQuery"
                type="info"
                showIcon
              />
            </Space>
          </Card>

          <Card style={{ marginTop: 20 }}>
            <Title level={4}>Raw Data (for debugging)</Title>
            <TextArea
              value={JSON.stringify(reformulatedData || parsedData, null, 2)}
              readOnly
              rows={10}
              style={{ fontFamily: "monospace", fontSize: "12px" }}
            />
          </Card>
        </div>
      )}

      {/* Multiple Workouts Display */}
      {mode === "program" && (parsedWorkouts.length > 0 || reformulatedWorkouts.length > 0) && (
        <div>
          <Card style={{ marginBottom: 20 }}>
            <Title level={3}>
              Program Workouts ({(reformulatedWorkouts.length > 0 ? reformulatedWorkouts : parsedWorkouts).length})
              <Tag color={reformulatedWorkouts.length > 0 ? "green" : "blue"} style={{ marginLeft: 8 }}>
                {reformulatedWorkouts.length > 0 ? "Reformulated" : "Original"}
              </Tag>
            </Title>

            {/* Global Category Editor */}
            <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text strong>Global Category Editor</Text>
                <Text type="secondary">
                  Change the category for all workouts at once. Current category: <strong>{programCategory}</strong>
                </Text>
                <Space>
                  <Input
                    placeholder="New category for all workouts"
                    value={programCategory}
                    onChange={(e) => setProgramCategory(e.target.value)}
                    style={{ width: 300 }}
                  />
                  <Button
                    onClick={() => applyCategoryToAll(programCategory)}
                    type="primary"
                  >
                    Apply to All Workouts
                  </Button>
                </Space>
              </Space>
            </Card>

            <Space style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={saveMultipleWorkouts}
                loading={saving}
                size="large"
              >
                Save All to SimplyRide
              </Button>
            </Space>

            <Collapse>
              {(reformulatedWorkouts.length > 0 ? reformulatedWorkouts : parsedWorkouts).map((workout, index) => (
                <Panel
                  key={index}
                  header={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <span>{workout.title}</span>
                      <Space>
                        <Tag>{workout.category}</Tag>
                        <Tag color="orange">{workout.steps?.length || 0} steps</Tag>
                        <Tag color="blue">{workout.stress_points} SP</Tag>
                      </Space>
                    </div>
                  }
                >
                  <Card size="small" style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <Text strong>Title: </Text>
                        <Input
                          value={workout.title}
                          onChange={(e) => updateWorkoutField(index, 'title', e.target.value)}
                          style={{ width: '100%', marginTop: 4 }}
                        />
                      </div>
                      <div>
                        <Text strong>Category: </Text>
                        <Input
                          value={workout.category}
                          onChange={(e) => updateWorkoutField(index, 'category', e.target.value)}
                          style={{ width: '100%', marginTop: 4 }}
                        />
                      </div>
                      <div>
                        <Text strong>Stress Points: </Text>
                        <Input
                          value={workout.stress_points || ''}
                          onChange={(e) => updateWorkoutField(index, 'stress_points', parseInt(e.target.value) || 0)}
                          style={{ width: '100%', marginTop: 4 }}
                          type="number"
                        />
                      </div>
                      <div>
                        <Text strong>Description: </Text>
                        <Input.TextArea
                          value={workout.description}
                          onChange={(e) => updateWorkoutField(index, 'description', e.target.value)}
                          rows={3}
                          style={{ width: '100%', marginTop: 4 }}
                        />
                      </div>
                      <div>
                        <Text strong>Steps ({workout.steps?.length || 0}):</Text>
                        <div style={{ marginTop: 8 }}>
                          <Table
                            columns={createStepColumns(index)}
                            dataSource={workout.steps || []}
                            rowKey={(record, stepIndex) => `${index}-${stepIndex}`}
                            pagination={false}
                            size="small"
                            scroll={{ x: 600 }}
                          />
                        </div>
                      </div>
                    </Space>
                  </Card>
                </Panel>
              ))}
            </Collapse>
          </Card>
        </div>
      )}

      {/* Category Selection Modal */}
      <Modal
        title="Select Category for Reformulation"
        open={categoryModalVisible}
        onOk={confirmReformulate}
        onCancel={() => setCategoryModalVisible(false)}
        okText="Reformulate with GPT"
        cancelText="Cancel"
        confirmLoading={reformulating}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            message="GPT Reformulation"
            description="This will use GPT to reformulate the workout name, description, and category by removing Zwift references and making it suitable for SimplyRide."
            type="info"
            showIcon
          />
          <Text>Current category: <strong>{parsedData?.category}</strong></Text>
          <Text>Select a new category (optional - leave empty to use GPT suggestion):</Text>
          <Select
            placeholder="Choose category"
            value={selectedCategory}
            onChange={setSelectedCategory}
            style={{ width: '100%' }}
            allowClear
          >
            <Select.Option value="Warm Up">Warm Up</Select.Option>
            <Select.Option value="Intervals">Intervals</Select.Option>
            <Select.Option value="Tempo">Tempo</Select.Option>
            <Select.Option value="Sweet Spot">Sweet Spot</Select.Option>
            <Select.Option value="Threshold">Threshold</Select.Option>
            <Select.Option value="VO2 Max">VO2 Max</Select.Option>
            <Select.Option value="Hill Climb">Hill Climb</Select.Option>
            <Select.Option value="Recovery">Recovery</Select.Option>
            <Select.Option value="Endurance">Endurance</Select.Option>
            <Select.Option value="Strength">Strength</Select.Option>
            <Select.Option value="Power">Power</Select.Option>
            <Select.Option value="Mixed">Mixed</Select.Option>
          </Select>
        </Space>
      </Modal>
    </div>
  );
}

export default WorkoutParser;
